import React, { useState, useEffect } from 'react'
import { FiLogOut, FiInfo, FiExternalLink, FiLogIn } from 'react-icons/fi'

interface SettingsProps {
  isAuthenticated: boolean
  onLogout: () => void
  onConnect: () => void
  apiKey: string
  onApiKeySubmit: (apiKey: string) => void
  accessToken: string | null
  isConfigured?: boolean
}

const Settings: React.FC<SettingsProps> = ({
  isAuthenticated,
  onLogout,
  onConnect,
  apiKey,
  onApiKeySubmit
}) => {
  const [inputApiKey, setInputApiKey] = useState(apiKey)
  const [expanded, setExpanded] = useState<'spotify' | 'gemini' | null>('spotify')
  
  useEffect(() => {
    setInputApiKey(apiKey)
  }, [apiKey])
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputApiKey.trim() && inputApiKey !== apiKey) {
      onApiKeySubmit(inputApiKey)
    }
  }
  
  return (
    <>
      <h2 className="text-xl font-bold mb-4">Settings</h2>
      
      <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Spotify Account</h3>
          <button 
            className="p-1"
            onClick={() => setExpanded(expanded === 'spotify' ? null : 'spotify')}
          >
            <svg 
              className={`h-5 w-5 transition-transform ${expanded === 'spotify' ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {expanded === 'spotify' && (
          <div className="mb-4">
            {isAuthenticated ? (
              <div className="space-y-4">
                <p className="text-spotify-text">You are currently connected to Spotify.</p>
                <button
                  onClick={onLogout}
                  className="flex items-center justify-center gap-2 bg-spotify-error hover:bg-opacity-90 text-white px-4 py-2 rounded-full transition-colors"
                >
                  <FiLogOut /> Disconnect
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-spotify-text">Connect to your Spotify account to create and play mood-based playlists.</p>
                <button
                  onClick={onConnect}
                  className="flex items-center justify-center gap-2 bg-spotify-green hover:bg-opacity-90 text-white px-4 py-2 rounded-full transition-colors"
                >
                  <FiLogIn /> Connect to Spotify
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Gemini API Key</h3>
          <button 
            className="p-1"
            onClick={() => setExpanded(expanded === 'gemini' ? null : 'gemini')}
          >
            <svg 
              className={`h-5 w-5 transition-transform ${expanded === 'gemini' ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {expanded === 'gemini' && (
          <div>
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <FiInfo className="text-spotify-text-gray mr-2" size={16} />
                <span className="text-sm text-spotify-text-gray">This key is required to generate personalized playlists</span>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={inputApiKey}
                  onChange={(e) => setInputApiKey(e.target.value)}
                  placeholder="Enter your Gemini API Key"
                  className="w-full bg-spotify-input text-spotify-text p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-spotify-green"
                />
              </div>
              
              <button
                type="submit"
                disabled={!inputApiKey.trim() || inputApiKey === apiKey}
                className="bg-spotify-green hover:bg-opacity-90 text-white px-4 py-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save API Key
              </button>
            </form>
            
            <div className="mt-4 text-sm text-spotify-text-gray">
              <p className="mb-2">
                <a 
                  href="https://ai.google.dev/tutorials/setup" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-spotify-green hover:underline flex items-center"
                >
                  <span>Get an API key from Google</span>
                  <FiExternalLink className="ml-1" />
                </a>
              </p>
              <p>
                Google offers free API credits that should be more than enough for personal use.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Settings 